import { Badge, Box, Divider, Drawer, SimpleGrid, Stack, Text } from "@mantine/core";
import type { CollectedFee } from "../../apis/tenantCollectedFee";
import { formatDate, formatPercent } from "../../utils/format";
import { CollectedFeeStatusBadge } from "./CollectedFeeStatusBadge";
import { MyCustomerName } from "./MyCustomerName";
import { Money } from "./Money";
import { OrderRefCell, orderCodeOf } from "./OrderRefCell";

interface Props {
  fee: CollectedFee | null;
  opened: boolean;
  onClose: () => void;
}

const METHOD_LABEL: Record<string, string> = {
  PERCENTAGE: "Хувь",
  FLAT      : "Тогтмол",
  TIERED    : "Шатлалт"
};

const REFUND_LABEL: Record<string, string> = {
  PRO_RATA : "Pro-rata",
  NONE     : "Буцаахгүй",
  FULL_ONLY: "Зөвхөн бүрэн буцаалт"
};

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <Stack gap={2}>
      <Text size="xs" c="dimmed">{label}</Text>
      {typeof value === "string" ? (
        <Text size="sm" style={{ fontVariantNumeric: "tabular-nums" }}>{value}</Text>
      ) : (
        <Box style={{ fontVariantNumeric: "tabular-nums" }}>{value}</Box>
      )}
    </Stack>
  );
}

export function CollectedFeeDetailDrawer({ fee, opened, onClose }: Props) {
  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size="lg"
      title={
        fee ? (
          <Stack gap={4}>
            <Text fw={600}>{orderCodeOf(fee) ?? `#${fee.orderId.slice(0, 8)}`}</Text>
            <CollectedFeeStatusBadge status={fee.status} />
          </Stack>
        ) : (
          "Цуглуулсан шимтгэл"
        )
      }
    >
      {!fee ? null : (
        <Stack gap="md">
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
            <Row label="Захиалга №" value={<OrderRefCell orderNumber={fee.orderNumber} orderId={fee.orderId} />} />
            <Row label="Харилцагч" value={<MyCustomerName id={fee.endCustomerBusinessId} />} />
            <Row label="Тооцооны арга" value={METHOD_LABEL[fee.calculationMethod] ?? fee.calculationMethod} />
            <Row label="Үндсэн дүн" value={<Money value={fee.baseAmount} currency={fee.baseCurrency} />} />
            {fee.appliedRate != null && fee.appliedRate !== "" && (
              <Row label="Хувь" value={formatPercent(Number(fee.appliedRate) * 100)} />
            )}
            {fee.appliedFlatAmount && (
              <Row label="Тогтмол дүн" value={<Money value={fee.appliedFlatAmount} currency={fee.feeCurrency} />} />
            )}
            <Row label="Тооцоолсон шимтгэл" value={<Money value={fee.calculatedFeeAmount} currency={fee.feeCurrency} />} />
            <Row
              label="Эцсийн шимтгэл"
              value={
                <Box>
                  <Money value={fee.finalFeeAmount} currency={fee.feeCurrency} fw={600} />
                  {(fee.minApplied || fee.maxApplied) && (
                    <Text size="xs" c="orange">
                      {fee.minApplied ? "Минимум хэрэглэсэн" : "Максимум хэрэглэсэн"}
                    </Text>
                  )}
                </Box>
              }
            />
            <Row label="Буцаалтын бодлого" value={REFUND_LABEL[fee.refundPolicySnapshot] ?? fee.refundPolicySnapshot} />
            <Row label="Цуглуулсан огноо" value={fee.collectedAt ? formatDate(fee.collectedAt) : "—"} />
          </SimpleGrid>

          {fee.status === "REFUNDED" && (
            <>
              <Divider label="Буцаалт" labelPosition="left" />
              <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                <Row label="Буцаасан огноо" value={fee.reversedAt ? formatDate(fee.reversedAt) : "—"} />
                <Row
                  label="Буцаасан дүн"
                  value={fee.reversedAmount ? <Money value={fee.reversedAmount} currency={fee.feeCurrency} /> : "—"}
                />
                <Box style={{ gridColumn: "1 / -1" }}>
                  <Row label="Буцаалтын шалтгаан" value={fee.reversalReason ?? "—"} />
                </Box>
              </SimpleGrid>
            </>
          )}

          <Badge variant="light" color="gray">Зөвхөн харах</Badge>
        </Stack>
      )}
    </Drawer>
  );
}
